import { inject, injectable } from "inversify";
import ApiRepo from "./api.repo";
import { ApiPage } from "@/core/models/page.model";
import { Province } from "../models/address/province.model";
import { District } from "../models/address/district.model";
import { Ward } from "../models/address/ward.model";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import { AppUserAreaType } from "../enums/global.enums";
import { selectWardOptions } from "../utils/ward-options";

import { factoryRuntimeSettings } from "../stores/factory-runtime-settings.store";

@injectable()
export class AddressRepo {
    @inject(ApiRepo)
    private apiRepo!: ApiRepo;
    provinces: Map<string, Province> = new Map();
    districts: Map<string, District> = new Map();
    wards: Map<string, Ward> = new Map();
    private referencedWards?: { factoryId: string; ids: string[] };

    private async getAllProvincesApi() {
        const response = await this.apiRepo.get<ApiPage<Province>>("/api/Province/all");
        if (response?.code === "00") {
            response.result.data.forEach(_record => {
                this.provinces.set(_record.id?.toString(), _record);
            })
        }
    }

    private async getAllDistrictsApi() {
        const response = await this.apiRepo.get<ApiPage<District>>("/api/District/all");
        if (response?.code === "00") {
            response.result.data.forEach(_record => {
                this.districts.set(_record.id?.toString(), _record);
            })
        }
    }

    private async getAllWardsApi() {
        const response = await this.apiRepo.get<ApiPage<Ward>>("/api/Ward/all");
        if (response?.code === "00") {
            response.result.data.forEach(_record => {
                this.wards.set(_record.id?.toString(), _record);
            })
        }
    }

    async getProvince(id: string) {
        if (this.provinces.size === 0) {
            console.log("Cache missed!");
            await this.getAllProvincesApi();
        }
        const record = this.provinces.get(id?.toString());
        return record;
    }

    async getDistrict(id: string) {
        if (this.districts.size === 0) {
            console.log("Cache missed!");
            await this.getAllDistrictsApi();
        }
        const record = this.districts.get(id?.toString());
        return record;
    }

    async getWard(id: string) {
        if (this.wards.size === 0) {
            console.log("Cache missed!");
            await this.getAllWardsApi();
        }
        const record = this.wards.get(id?.toString());
        return record;
    }

    async getAllProvinces(selectedProvinceId?: string) {
        if (this.provinces.size === 0) {
            console.log("Cache missed!");
            await this.getAllProvincesApi();
        }
        const allowedProvinces = GlobalUtils.parseArray(factoryRuntimeSettings.string("allowedArea", process.env.REACT_APP_ALLOWED_AREA))
            .filter(area => area.split("_")[0] === AppUserAreaType.Province)
            .map(area => area.split("_")[1]).filter(Boolean);
        const records = Array.from(this.provinces).filter(([key]) => {
            if (allowedProvinces.length === 0) return true;
            return key === selectedProvinceId || allowedProvinces.includes(key);
        });
        return records;
    }

    async getAllDistricts(provinceId?: string) {
        if (this.districts.size === 0) {
            console.log("Cache missed!");
            await this.getAllDistrictsApi();
        }
        const records = Array.from(this.districts);
        if (!provinceId) {
            return records;
        }
        return records.filter(e => e[1].provinceId === provinceId);
    }

    async getAllWards(districtId?: string) {
        if (this.wards.size === 0) {
            console.log("Cache missed!");
            await this.getAllWardsApi();
        }
        const records = Array.from(this.wards);
        if (!districtId) {
            return records;
        }
        return records.filter(e => e[1].districtId === districtId);;
    }

    // Prefer old namesakes for choices; retain the saved ward and any ward this factory already uses.
    async getWardsByProvinceId(provinceId?: string, selectedWardId?: string, preservedWardIds: readonly string[] = []): Promise<Ward[]> {
        if (!provinceId && !selectedWardId && preservedWardIds.length === 0) return [];
        const records = await this.getAllWards();
        const referenced = await this.factoryReferencedWardIds();
        const preserved = [...referenced, ...preservedWardIds, ...(selectedWardId ? [selectedWardId] : [])];
        const preservedSet = new Set(preserved);
        const wards = records.filter(([id, ward]) => ward.provinceId === provinceId || preservedSet.has(id)).map(([, ward]) => ward);
        return selectWardOptions(wards, preserved);
    }

    // Wards in the selected province that already have a toll area of the current factory.
    // The full province list stays on getWardsByProvinceId / Ward/get-by-provinceId.
    async getFactoryWardsByProvinceId(provinceId?: string, selectedWardId?: string): Promise<Ward[]> {
        if (!provinceId && !selectedWardId) return [];
        let wards: Ward[] = [];
        if (provinceId && this.apiRepo) {
            const response = await this.apiRepo.get<ApiPage<Ward>>("/api/Ward/get-by-provinceId-of-factory", { provinceId });
            if (response?.code === "00") {
                wards = response.result?.data ?? [];
            }
        }
        if (selectedWardId && !wards.some(ward => ward.id === selectedWardId)) {
            const selected = await this.getWard(selectedWardId);
            if (selected) wards = [selected, ...wards];
        }
        return [...wards].sort((left, right) => (left.name || "").localeCompare(right.name || "", "vi"));
    }

    private async factoryReferencedWardIds(): Promise<string[]> {
        const factoryId = factoryRuntimeSettings.getSnapshot().factoryId || "";
        if (this.referencedWards?.factoryId === factoryId) return this.referencedWards.ids;
        let ids: string[] = [];
        try {
            if (this.apiRepo) {
                const response = await this.apiRepo.get<ApiPage<{ wardId?: string }>>("/api/TollArea/all");
                if (response?.code === "00") {
                    ids = response.result.data.map(area => area.wardId).filter((id): id is string => !!id);
                }
            }
        } catch {
            ids = [];
        }
        this.referencedWards = { factoryId, ids };
        return ids;
    }

    async getFactoryWards() {
        const response = await this.apiRepo.get<ApiPage<Ward>>("/api/Ward/get-by-water-factory");
        if (response?.code === "00") {
            return response.result.data;
        }
        return [];
    }

    async getDistrictsByProvinceId(provinceId: string): Promise<District[]> {
        if (!provinceId) return [];
        const response = await this.apiRepo.get<ApiPage<District>>("/api/District/get-by-provinceId", { provinceId });
        if (response?.code === "00") {
            return response.result.data;
        }
        return [];
    }

    async getWardsByDistrictId(districtId: string): Promise<Ward[]> {
        if (!districtId) return [];
        const response = await this.apiRepo.get<ApiPage<Ward>>("/api/Ward/get-by-districtId", { districtId });
        if (response?.code === "00") {
            return response.result.data;
        }
        return [];
    }
}
